import { getElevation, getReferenceElevation } from "../utils/elevation";
import { syncFacadeHeights } from "../../config/facades";

// Feste Gebaeudehoehe aus create_geometries.py (Building.height = 8). Steht
// hier nur, um sie in der Vorschau SICHTBAR zu machen — die Zahl ist eine
// Annahme, und bisher hat man ihr im UI nirgends angesehen, dass sie eine ist.
const ANGENOMMENE_GEBAEUDEHOEHE_M = 8;

export class BuildingsPolyLineTool {
  constructor(map, setGeometries, getGeometries = () => ({})) {
    this.map = map;
    this.setGeometries = setGeometries;
    this.getGeometries = getGeometries;
    this.currentLine = [];
    this.lines = [];

    // ── Hoehen-Vorschau (Signify-Feedback 04.08.2026) ─────────────────
    // Beim Zeichnen von Fassaden war bisher nirgends zu sehen, ob ein
    // Gebaeude ueber oder unter dem Spielfeldniveau liegt. Genau diese
    // relative Hoehe entscheidet aber ueber die Fassadenbewertung (ks/Ev)
    // — ein Haus 6 m ueber dem Feld bekommt voll die Keule ab, eines 6 m
    // darunter kaum.
    this.referenceElevation = null;   // Spielfeldhoehe in m ue. NN
    this.vertexElevations = [];       // Hoehen der gesetzten Stuetzpunkte
    this.hoverElevation = null;
    // Laufende Nummer gegen Wettlaeufe: Hoehenabfragen sind asynchron, bei
    // schneller Mausbewegung kann eine aeltere Antwort nach einer neueren
    // eintreffen und die Anzeige zurueckspringen lassen.
    this._hoverSeq = 0;

    this.sourceId = "building-preview-source";
    this.layerId = "building-preview-layer";

    if (!this.map.getSource(this.sourceId)) {
      this.map.addSource(this.sourceId, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      this.map.addLayer({
        id: this.layerId,
        type: "line",
        source: this.sourceId,
        paint: {
          "line-color": "#FF0000",
          "line-width": 2,
        },
      });
    }

    this.rightClickHandler = (e) => {
      e.preventDefault();
      this.finishCurrentLine();
    };
    this.map.getCanvas().addEventListener("contextmenu", this.rightClickHandler);

    // Anzeigefeld fuer die Hoehen. Bewusst als DOM-Overlay und nicht als
    // MapLibre-Symbol-Layer: unser Kartenstil (MapInit.js) hat keine
    // glyphs-Quelle, Textlayer wuerden schlicht nichts rendern.
    this.elevationBox = document.createElement("div");
    // Oben links: unten links sitzt der Wayback-Regler (WaybackControl.js,
    // left 10 / bottom 10), oben mittig das Mast-im-Feld-Warnbanner
    // (PointTool). Hier kollidiert nichts.
    this.elevationBox.style.cssText = `
      position: absolute;
      left: 10px;
      top: 10px;
      z-index: 10;
      background: rgba(16, 24, 40, 0.88);
      color: #fff;
      padding: 8px 10px;
      border-radius: 6px;
      font-family: system-ui, sans-serif;
      font-size: 12px;
      line-height: 1.5;
      white-space: nowrap;
      pointer-events: none;
      display: none;
      box-shadow: 0 2px 8px rgba(0,0,0,0.25);
    `;
    this.map.getCanvas().parentElement.appendChild(this.elevationBox);
  }

  // Bezugshoehe = mittlere Gelaendehoehe an den Ecken des Spielfelds. Einmal
  // je gezeichnetem Feld ermittelt und dann festgehalten.
  async _ensureReference() {
    if (this.referenceElevation !== null) return this.referenceElevation;
    const rect = this.getGeometries()?.fieldRectangle;
    if (!rect || rect.length < 3) return null;
    this.referenceElevation = await getReferenceElevation(rect);
    return this.referenceElevation;
  }

  // Formatiert eine Hoehendifferenz mit Vorzeichen — "+2,3 m" / "-0,8 m".
  // Komma als Dezimaltrenner, der Rest der Oberflaeche ist auch deutsch.
  static _formatDelta(d) {
    return `${d >= 0 ? "+" : "-"}${Math.abs(d).toFixed(1).replace(".", ",")} m`;
  }

  _renderElevationBox() {
    const ref = this.referenceElevation;
    if (ref === null) {
      this.elevationBox.style.display = "none";
      return;
    }

    const F = BuildingsPolyLineTool._formatDelta;
    const zeilen = [];

    if (this.hoverElevation !== null) {
      const d = this.hoverElevation - ref;
      const oberkante = d + ANGENOMMENE_GEBAEUDEHOEHE_M;
      zeilen.push(
        `<b>Gelände hier: ${F(d)}</b> zum Spielfeld`,
        `<span style="opacity:.75">Oberkante bei ${ANGENOMMENE_GEBAEUDEHOEHE_M} m Gebäudehöhe: ${F(oberkante)}</span>`
      );
    }

    // Verlauf entlang der schon gesetzten Stuetzpunkte — daran sieht man das
    // Gefaelle einer Fassade, und genau das ist fuer die Bewertung relevant.
    const gesetzt = this.vertexElevations.filter((v) => v !== null);
    if (gesetzt.length) {
      const deltas = gesetzt.map((v) => F(v - ref)).join("  ·  ");
      zeilen.push(`<span style="opacity:.75">Stützpunkte: ${deltas}</span>`);
    }

    zeilen.push(
      `<span style="opacity:.55">Spielfeld ${ref.toFixed(1).replace(".", ",")} m ü. NN</span>`
    );

    this.elevationBox.innerHTML = zeilen.join("<br>");
    this.elevationBox.style.display = "block";
  }

  onClick(lngLat) {
    this.currentLine.push([lngLat.lng, lngLat.lat]);
    this.updatePreview();

    // Hoehe des gesetzten Stuetzpunkts festhalten (Platzhalter sofort, damit
    // die Reihenfolge stimmt, auch wenn die Antworten verdreht eintreffen).
    const slot = this.vertexElevations.push(null) - 1;
    this._ensureReference().then(() =>
      getElevation(lngLat.lng, lngLat.lat).then((h) => {
        this.vertexElevations[slot] = h;
        this._renderElevationBox();
      })
    );
  }

  onMouseMove(lngLat) {
    // Die Hoehenanzeige laeuft AUCH vor dem ersten Klick — sonst muesste man
    // erst blind einen Punkt setzen, um zu sehen, wie hoch es dort liegt.
    const seq = ++this._hoverSeq;
    this._ensureReference().then(() =>
      getElevation(lngLat.lng, lngLat.lat).then((h) => {
        if (seq !== this._hoverSeq) return;   // veraltete Antwort verwerfen
        this.hoverElevation = h;
        this._renderElevationBox();
      })
    );

    if (this.currentLine.length === 0) return;
    const tempLine = [...this.currentLine, [lngLat.lng, lngLat.lat]];
    this.updatePreview(tempLine);
  }

  finishCurrentLine() {
    if (this.currentLine.length < 2) {
      this.currentLine = [];
      this.clearPreview();
      return;
    }

    this.lines.push([...this.currentLine]);
    this.currentLine = [];
    // Naechste Fassade faengt mit leerem Verlauf an; die Bezugshoehe des
    // Spielfelds bleibt natuerlich stehen.
    this.vertexElevations = [];
    this._renderElevationBox();

    this.clearPreview();
    this.pushToState();
  }

  // ── Fassaden + Hoehen immer zusammen in den State schreiben ────────
  // Die Fassaden-Tabelle in der Sidebar liest buildingPolylines und
  // buildingHeights index-parallel (Fassade i <-> Hoehe i). Wuerde nur die
  // Linienliste geschrieben, stuenden die eingetragenen Hoehen nach dem
  // naechsten Zeichnen an der falschen Fassade.
  pushToState() {
    this.setGeometries((prev) => ({
      ...prev,
      buildingPolylines: [...this.lines],
      buildingHeights: syncFacadeHeights(prev.buildingHeights, this.lines.length),
    }));
  }

  // Eine einzelne fertige Fassade entfernen (X-Knopf in der Tabelle).
  // Undo nimmt nur die zuletzt gezeichnete zurueck — hier darf es jede sein.
  removeLine(index) {
    if (index < 0 || index >= this.lines.length) return false;
    this.lines.splice(index, 1);
    this.updatePreview();
    this.setGeometries((prev) => {
      const hs = [...(prev.buildingHeights || [])];
      hs.splice(index, 1);
      return {
        ...prev,
        buildingPolylines: [...this.lines],
        buildingHeights: syncFacadeHeights(hs, this.lines.length),
      };
    });
    return true;
  }

  // Alle Fassaden verwerfen — Werkzeug UND State. Die Sidebar leerte beim
  // Zuruecksprung auf Schritt 5 bisher nur den State; das Werkzeug behielt
  // seine `lines`, malte sie weiter rot und haengte sie beim naechsten
  // "fertig" wieder an. Durch die Tabelle wird das sichtbar.
  clear() {
    this.reset();
    this.setGeometries((prev) => ({
      ...prev,
      buildingPolylines: [],
      buildingHeights: [],
    }));
  }

  // ── Undo (Signify-Feedback Dennis 03.07.2026) ────────────────────
  // 1. Priorität: letzten Punkt der GERADE gezeichneten Linie zurücknehmen.
  // 2. Sonst: die zuletzt FERTIGE Fassade komplett entfernen.
  undo() {
    if (this.currentLine.length > 0) {
      this.currentLine.pop();
      this.vertexElevations.pop();
      this._renderElevationBox();
      // Einzelner Punkt ist kein gültiger LineString — erst ab 2 zeichnen
      this.updatePreview(this.currentLine.length >= 2 ? this.currentLine : null);
      return true;
    }
    if (this.lines.length > 0) {
      this.lines.pop();
      this.updatePreview();
      this.pushToState();
      return true;
    }
    return false;
  }

  updatePreview(tempLine = null) {
    const features = [...this.lines];
    if (tempLine) features.push(tempLine);

    const geojson = {
      type: "FeatureCollection",
      features: features.map((line) => ({
        type: "Feature",
        properties: {},
        geometry: { type: "LineString", coordinates: line },
      })),
    };

    if (this.map.getSource(this.sourceId)) {
      this.map.getSource(this.sourceId).setData(geojson);
    }
  }

  clearPreview() {
    if (this.map.getSource(this.sourceId)) {
      this.map.getSource(this.sourceId).setData({ type: "FeatureCollection", features: [] });
    }
  }

  // Beim Zuruecksetzen der Skizze muss auch die Bezugshoehe weg — sonst
  // rechnet die Vorschau am naechsten Feld gegen die Hoehe des alten.
  reset() {
    this.currentLine = [];
    this.lines = [];
    this.referenceElevation = null;
    this.vertexElevations = [];
    this.hoverElevation = null;
    this.clearPreview();
    this.elevationBox.style.display = "none";
  }

  destroy() {
    this.map.getCanvas().removeEventListener("contextmenu", this.rightClickHandler);
    if (this.map.getLayer(this.layerId)) this.map.removeLayer(this.layerId);
    if (this.map.getSource(this.sourceId)) this.map.removeSource(this.sourceId);
    this.elevationBox?.remove();
  }
}